// The Capability map's PERMISSION BUCKETS + AREA roll-ups (Capability Map v2, the "Signal" re-layout) —
// pure display derivations over the generated-registry projection (P-MAPPED, G-148). The three buckets are
// the SAME three-way split the map has always rendered, restated in plain English: a gated capability
// stops at a Gate a named human must exercise ("Asks you first"); an ungated write stays inside the
// Fence's guardrails ("Acts on its own"); a read changes nothing ("Only looks"). Nothing here judges —
// each bucket is a restatement of the projection's own `gated`/`effect` flags (G-182). The area label is
// a DISPLAY label over the owner module-directory value the projection already carries (G-197); an
// unknown owner falls back to the raw label rather than being hidden (P-SELF).

import type { CapabilityEntryView } from '../operate-projections.ts';

export type CapabilityBucket = 'ask' | 'act' | 'look';
export const BUCKET_ORDER: readonly CapabilityBucket[] = ['ask', 'act', 'look'];

/** Plain-English meta for one bucket — accurate restatements of the gated/effect flags, never a claim. */
export interface BucketMeta {
  readonly label: string;
  readonly who: string;
  readonly note: string;
}

export const BUCKET_META: Readonly<Record<CapabilityBucket, BucketMeta>> = {
  ask: {
    label: 'Asks you first',
    who: 'A named person approves it first',
    note: 'Ritona can prepare and propose these, then it stops. The owning module holds a Gate a named person has to exercise before anything moves.',
  },
  act: {
    label: 'Acts on its own',
    who: 'Ritona, without an approval',
    note: 'Writes the Fence allows without a human gate — they run inside its guardrails and stay visible in Monitoring.',
  },
  look: {
    label: 'Only looks',
    who: 'Read only — no change',
    note: 'Reads and reports. Nothing changes in your business or in any connected system.',
  },
};

/** The bucket a capability belongs to — a restatement of its recorded flags, never a judgment. */
export function bucketOf(cap: CapabilityEntryView): CapabilityBucket {
  if (cap.gated) return 'ask';
  return cap.effect === 'write' ? 'act' : 'look';
}

/** Display labels for the owner module-directory values the registry carries; unknown owners pass through. */
const AREA_LABELS: Readonly<Record<string, string>> = {
  'modules/tasks': 'Tasks',
  'modules/metrics': 'KPIs',
  'modules/scheduler': 'Scheduler',
  'modules/comms': 'Email',
  'modules/memory': 'Memory',
  'modules/artifacts': 'Files',
  'modules/integrations': 'Connectors',
  'modules/identity': 'Access',
  'modules/work': 'Workflows',
  'modules/governance': 'Governance',
  'modules/simulated-systems': 'Demo systems',
  'modules/agent': 'Agent',
  'kernel/security': 'Guardrails',
  composition: 'Agent tools',
};

export function areaLabelOf(owner: string): string {
  return AREA_LABELS[owner] ?? owner;
}

/** A human kind label (display only — the enum stays the machine key). */
export function kindLabelOf(kind: CapabilityEntryView['kind']): string {
  return kind === 'route'
    ? 'A dashboard endpoint'
    : kind === 'tool'
      ? 'A tool the agent calls'
      : 'A skill';
}

/** Count each bucket across a capability set. */
export function bucketCounts(
  caps: readonly CapabilityEntryView[],
): Readonly<Record<CapabilityBucket, number>> {
  const counts = { ask: 0, act: 0, look: 0 };
  for (const cap of caps) counts[bucketOf(cap)] += 1;
  return counts;
}

/** One area-rail row: the owner key, its display label, and its bucket mix over the given set. */
export interface AreaAggregate {
  readonly key: string;
  readonly label: string;
  readonly total: number;
  readonly mix: Readonly<Record<CapabilityBucket, number>>;
}

/** Aggregate the given capability set per owner, sorted by display label (stable, projection-driven). */
export function areaAggregates(caps: readonly CapabilityEntryView[]): readonly AreaAggregate[] {
  const byOwner = new Map<string, CapabilityEntryView[]>();
  for (const cap of caps) {
    const list = byOwner.get(cap.owner) ?? [];
    list.push(cap);
    byOwner.set(cap.owner, list);
  }
  return [...byOwner.entries()]
    .map(([owner, list]) => ({
      key: owner,
      label: areaLabelOf(owner),
      total: list.length,
      mix: bucketCounts(list),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Case-insensitive search over the fields the surface renders (summary, id, labels — display only). */
export function searchCapabilities(
  caps: readonly CapabilityEntryView[],
  query: string,
): readonly CapabilityEntryView[] {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return caps;
  return caps.filter((cap) => {
    const hay = `${cap.summary} ${cap.id} ${cap.owner} ${areaLabelOf(cap.owner)} ${cap.kind} ${BUCKET_META[bucketOf(cap)].label}`;
    return hay.toLowerCase().includes(needle);
  });
}
